/**
 * Length Range Slider - Works with facet-filters.js AJAX system
 */

if (!customElements.get('length-range')) {
  class LengthRange extends HTMLElement {
    constructor() {
      super();
      this.boundHandleAjaxUpdate = this.handleAjaxUpdate.bind(this);
      
      if (window.initLazyScript) {
        window.initLazyScript(this, this.init.bind(this));
      } else {
        this.init();
      }
    }

    init() {
      this.minNumberInput = this.querySelector('[data-type="min"].length-range__number-input');
      this.maxNumberInput = this.querySelector('[data-type="max"].length-range__number-input');
      this.minSliderInput = this.querySelector('[data-type="min"].length-range__slider-input');
      this.maxSliderInput = this.querySelector('[data-type="max"].length-range__slider-input');
      this.hiddenMin = this.querySelector('.length-range__hidden-min');
      this.hiddenMax = this.querySelector('.length-range__hidden-max');
      this.checkboxes = this.querySelectorAll('.length-range__checkbox');
      
      this.minValue = Number(this.dataset.rangeMin) || 0;
      this.maxValue = Number(this.dataset.rangeMax) || 1000;
      this.unit = this.dataset.unit || 'cm';
      this.filterLabel = this.closest('.filter')?.querySelector('.font-bold')?.textContent?.trim() || 'Pituus cm';
      this.timeout = null;
      
      // Event listeners for sliders
      this.minSliderInput?.addEventListener('input', this.handleSliderInput.bind(this));
      this.maxSliderInput?.addEventListener('input', this.handleSliderInput.bind(this));
      this.minSliderInput?.addEventListener('change', this.handleChange.bind(this));
      this.maxSliderInput?.addEventListener('change', this.handleChange.bind(this));
      
      // Event listeners for number inputs - debounced auto-apply
      this.minNumberInput?.addEventListener('input', this.handleNumberInputDebounced.bind(this));
      this.maxNumberInput?.addEventListener('input', this.handleNumberInputDebounced.bind(this));
      
      // Restore from URL on init (for page refresh / direct URL access)
      this.restoreFromUrl();
      this.updateSliderTrack();
      this.updateActiveFiltersDisplay();
      this.setupClearHandlers();
      
      // Update clear button visibility based on current state
      const isActive = parseInt(this.minSliderInput.value) !== this.minValue || 
                       parseInt(this.maxSliderInput.value) !== this.maxValue;
      this.updateClearButtonVisibility(isActive);
      
      // Listen for AJAX updates
      document.addEventListener('on:facet-filters:updated', this.boundHandleAjaxUpdate);
    }

    disconnectedCallback() {
      // Clean up event listener when element is removed
      document.removeEventListener('on:facet-filters:updated', this.boundHandleAjaxUpdate);
    }

    handleAjaxUpdate() {
      // Wait for URL to be updated by facet-filters.js
      setTimeout(() => {
        this.restoreFromUrl();
        this.updateSliderTrack();
        this.updateActiveFiltersDisplay();
      }, 50);
    }

    /**
     * Restore slider values from URL params
     */
    restoreFromUrl() {
      const url = new URL(window.location.href);
      const urlMin = url.searchParams.get('length_min');
      const urlMax = url.searchParams.get('length_max');
      
      if (urlMin !== null) {
        const minVal = parseInt(urlMin);
        if (!isNaN(minVal) && minVal >= this.minValue && minVal <= this.maxValue) {
          this.minSliderInput.value = minVal;
          this.minNumberInput.value = minVal;
          if (this.hiddenMin) this.hiddenMin.value = minVal;
        }
      }
      
      if (urlMax !== null) {
        const maxVal = parseInt(urlMax);
        if (!isNaN(maxVal) && maxVal >= this.minValue && maxVal <= this.maxValue) {
          this.maxSliderInput.value = maxVal;
          this.maxNumberInput.value = maxVal;
          if (this.hiddenMax) this.hiddenMax.value = maxVal;
        }
      }
    }

    /**
     * Handle slider drag - visual update only
     */
    handleSliderInput(evt) {
      const type = evt.target.dataset.type;
      let minVal = parseInt(this.minSliderInput.value);
      let maxVal = parseInt(this.maxSliderInput.value);

      // Prevent crossing
      if (type === 'min' && minVal > maxVal) {
        minVal = maxVal;
        this.minSliderInput.value = minVal;
      }
      if (type === 'max' && maxVal < minVal) {
        maxVal = minVal;
        this.maxSliderInput.value = maxVal;
      }

      // Sync number inputs
      this.minNumberInput.value = minVal !== this.minValue ? minVal : '';
      this.maxNumberInput.value = maxVal !== this.maxValue ? maxVal : '';
      
      this.updateSliderTrack();
    }

    /**
     * Handle number input with debounced filter apply
     */
    handleNumberInputDebounced(evt) {
      const type = evt.target.dataset.type;
      const value = parseInt(evt.target.value);
      
      // Visual update - sync slider
      if (!isNaN(value)) {
        const clampedValue = Math.max(this.minValue, Math.min(value, this.maxValue));
        
        if (type === 'min') {
          const maxVal = parseInt(this.maxSliderInput.value);
          this.minSliderInput.value = Math.min(clampedValue, maxVal);
        } else {
          const minVal = parseInt(this.minSliderInput.value);
          this.maxSliderInput.value = Math.max(clampedValue, minVal);
        }
        this.updateSliderTrack();
      }
      
      // Debounced filter apply (same delay as price filter)
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        this.applyFilter();
      }, 500);
    }

    /**
     * Handle change event - apply filter (for slider release)
     */
    handleChange() {
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        this.applyFilter();
      }, 100);
    }

    /**
     * Update slider track visualization
     */
    updateSliderTrack() {
      const min = parseInt(this.minSliderInput.value);
      const max = parseInt(this.maxSliderInput.value);
      const range = this.maxValue - this.minValue;
      
      if (range <= 0) return;
      
      const minPercent = ((min - this.minValue) / range) * 100;
      const maxPercent = ((max - this.minValue) / range) * 100;

      this.style.setProperty('--range-min', `${minPercent}%`);
      this.style.setProperty('--range-max', `${maxPercent}%`);
    }

    /**
     * Apply filter
     */
    applyFilter() {
      const minVal = parseInt(this.minSliderInput.value) || this.minValue;
      const maxVal = parseInt(this.maxSliderInput.value) || this.maxValue;
      
      // Update hidden inputs
      if (this.hiddenMin) {
        this.hiddenMin.value = minVal !== this.minValue ? minVal : '';
      }
      if (this.hiddenMax) {
        this.hiddenMax.value = maxVal !== this.maxValue ? maxVal : '';
      }
      
      // Update checkboxes
      this.checkboxes.forEach(checkbox => {
        const value = parseFloat(checkbox.dataset.value);
        if (isNaN(value) || checkbox.disabled) return;
        checkbox.checked = value >= minVal && value <= maxVal;
      });
      
      // Trigger facet-filters.js
      const firstCheckbox = this.querySelector('.length-range__checkbox:not([disabled])');
      if (firstCheckbox) {
        firstCheckbox.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    /**
     * Setup clear button handlers
     */
    setupClearHandlers() {
      // Use capture phase to run BEFORE theme's event handlers
      document.addEventListener('click', (evt) => {
        const link = evt.target.closest('a');
        if (!link) return;
        
        // Handle our own length filter clear
        if (link.classList.contains('js-clear-length-filter')) {
          evt.preventDefault();
          evt.stopPropagation();
          this.clearFilter();
          return;
        }
        
        // Handle length filter section clear button
        const filterContainer = this.closest('.filter');
        const clearBtn = filterContainer?.querySelector('.js-clear-filter');
        if (link === clearBtn) {
          evt.preventDefault();
          evt.stopPropagation();
          this.clearFilter();
          return;
        }
        
        // For OTHER filter clear buttons, preserve our length params in their URL
        if (link.classList.contains('js-clear-filter') && link !== clearBtn) {
          this.preserveLengthParamsInUrl(link);
        }
        
        // For active filter removal links (not ours), preserve length params
        if (link.classList.contains('active-filter') && !link.classList.contains('js-length-filter-tag')) {
          this.preserveLengthParamsInUrl(link);
        }
      }, true); // capture phase
    }

    /**
     * Add current length params to a link's href
     */
    preserveLengthParamsInUrl(link) {
      const currentUrl = new URL(window.location.href);
      const lengthMin = currentUrl.searchParams.get('length_min');
      const lengthMax = currentUrl.searchParams.get('length_max');
      
      if (lengthMin || lengthMax) {
        try {
          const linkUrl = new URL(link.href);
          if (lengthMin) linkUrl.searchParams.set('length_min', lengthMin);
          if (lengthMax) linkUrl.searchParams.set('length_max', lengthMax);
          link.href = linkUrl.toString();
        } catch (e) {
          // Invalid URL, ignore
        }
      }
    }

    clearFilter() {
      this.minSliderInput.value = this.minValue;
      this.maxSliderInput.value = this.maxValue;
      this.minNumberInput.value = '';
      this.maxNumberInput.value = '';
      if (this.hiddenMin) this.hiddenMin.value = '';
      if (this.hiddenMax) this.hiddenMax.value = '';
      
      this.checkboxes.forEach(cb => cb.checked = false);
      this.updateSliderTrack();
      this.updateClearButtonVisibility(false);
      
      const firstCheckbox = this.querySelector('.length-range__checkbox:not([disabled])');
      if (firstCheckbox) {
        firstCheckbox.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    /**
     * Update active filters display
     */
    updateActiveFiltersDisplay() {
      const minVal = parseInt(this.minSliderInput.value);
      const maxVal = parseInt(this.maxSliderInput.value);
      const isActive = minVal !== this.minValue || maxVal !== this.maxValue;
      
      document.querySelector('.active-filter.js-length-filter-tag')?.remove();
      
      if (!isActive) {
        this.updateActiveFiltersVisibility();
        return;
      }
      
      const container = document.querySelector('.active-filters');
      const wrapper = document.querySelector('.facets__active-filters');
      
      if (!container) return;
      
      const tag = document.createElement('a');
      tag.href = '#';
      tag.className = 'active-filter flex items-center text-current js-length-filter-tag js-clear-length-filter';
      
      // Use the same icon as theme's other active filters
      const iconHtml = '<svg width="24" height="24" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5" fill="none" fill-rule="evenodd" stroke-linejoin="round" aria-hidden="true" focusable="false" role="presentation" class="icon"><path d="M5 19 19 5M5 5l14 14"></path></svg>';
      
      tag.innerHTML = `${iconHtml}${this.filterLabel}: ${minVal} - ${maxVal} ${this.unit}`;
      
      const clearAll = container.querySelector('.js-clear-all');
      if (clearAll) {
        container.insertBefore(tag, clearAll);
      } else {
        container.appendChild(tag);
      }
      
      if (wrapper) wrapper.removeAttribute('hidden');
      this.updateActiveFiltersCount();
      
      // Also update the clear button visibility in the filter section
      this.updateClearButtonVisibility(isActive);
    }

    /**
     * Update clear button visibility in filter section
     */
    updateClearButtonVisibility(isActive) {
      const filterContainer = this.closest('.filter');
      const clearBtn = filterContainer?.querySelector('.js-clear-filter');
      if (clearBtn) {
        if (isActive) {
          clearBtn.removeAttribute('hidden');
        } else {
          clearBtn.setAttribute('hidden', '');
        }
      }
    }

    updateActiveFiltersCount() {
      const header = document.querySelector('.active-filters-header');
      if (!header) return;
      
      const count = document.querySelectorAll('.active-filters .active-filter').length;
      if (count > 0) {
        header.textContent = count === 1 ? '1 Käytetty suodatin' : `${count} Käytettyä suodatinta`;
      }
    }

    updateActiveFiltersVisibility() {
      const wrapper = document.querySelector('.facets__active-filters');
      const filters = document.querySelectorAll('.active-filters .active-filter');
      if (wrapper && filters.length === 0) {
        wrapper.setAttribute('hidden', '');
      }
      // Also hide the clear button when no active filters
      this.updateClearButtonVisibility(false);
    }
  }

  customElements.define('length-range', LengthRange);
}